import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FolderOpen, Plus, Trash2, Lock, Clock, Copy, Upload, Loader2, FileText, Image as ImageIcon, Film, FileArchive, File as FileIcon, X, Zap, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const CHUNK_SIZE = 5 * 1024 * 1024;

function fileIcon(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.includes("pdf")) return FileText;
  if (ct.includes("zip")) return FileArchive;
  return FileIcon;
}
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`);
const portalUrl = (token) => `${window.location.origin}/portal/${token}`;

async function uploadFile(gid, file, onProgress) {
  const { data: init } = await api.post(`/deliverables/${gid}/upload/init`, { filename: file.name, content_type: file.type || "application/octet-stream", size: file.size });
  let offset = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + CHUNK_SIZE);
    await api.put(`/deliverables/upload/${init.upload_id}/chunk`, chunk, { headers: { "Content-Type": "application/octet-stream" } });
    offset += CHUNK_SIZE;
    onProgress(Math.min(99, Math.round((offset / file.size) * 100)));
  }
  const { data } = await api.post(`/deliverables/upload/${init.upload_id}/complete`);
  onProgress(100);
  return data;
}

function UpgradeGate() {
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={FolderOpen}
      title="Client Portal is a Business feature"
      description="Share final photos, videos, and documents with clients through a secure download link — no account needed on their end. Upgrade to BizForge Command to unlock deliverables."
      action={<Button onClick={() => navigate("/settings")} data-testid="deliverables-upgrade-button"><Zap className="mr-2 h-4 w-4" /> Upgrade to BizForge Command</Button>}
      testId="deliverables-upgrade-gate"
    />
  );
}

export default function Deliverables() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [manageId, setManageId] = useState(null);
  const isBusiness = user?.plan === "business";

  const { data: galleries = [], isLoading } = useQuery({
    queryKey: ["deliverables"],
    queryFn: async () => (await api.get("/deliverables")).data,
    enabled: isBusiness,
  });

  const copyLink = (token) => { navigator.clipboard.writeText(portalUrl(token)); toast.success("Portal link copied — share it with your client."); };
  const remove = async (id) => {
    if (!window.confirm("Delete this gallery and all its files? This can't be undone.")) return;
    await api.delete(`/deliverables/${id}`);
    toast.success("Gallery deleted");
    qc.invalidateQueries({ queryKey: ["deliverables"] });
  };

  return (
    <div>
      <PageHeader
        title="Deliverables"
        subtitle="Share final files with clients through a secure portal link."
        testId="deliverables-header"
        actions={isBusiness ? <Button onClick={() => setCreateOpen(true)} data-testid="new-gallery-button"><Plus className="mr-2 h-4 w-4" /> New gallery</Button> : null}
      />

      {!isBusiness ? <UpgradeGate /> : isLoading ? <PageLoader /> : galleries.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No galleries yet" description="Create a gallery, upload the client's files, and send them a secure link." action={<Button onClick={() => setCreateOpen(true)}><Plus className="mr-2 h-4 w-4" /> New gallery</Button>} testId="deliverables-empty" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {galleries.map((g) => (
            <div key={g.id} className="flex flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`gallery-card-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 text-accent"><FolderOpen className="h-5 w-5" /></div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(g.id)} data-testid={`delete-gallery-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <h3 className="mt-3 font-display text-base font-semibold leading-tight">{g.title}</h3>
              {g.client_name && <p className="mt-0.5 text-sm text-muted-foreground">{g.client_name}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <Badge variant="secondary" className="gap-1">{g.file_count} file{g.file_count === 1 ? "" : "s"}</Badge>
                {g.has_access_code && <Badge variant="outline" className="gap-1"><Lock className="h-3 w-3" /> Code</Badge>}
                {g.expires_at && <Badge variant="outline" className={`gap-1 ${g.expired ? "border-rose-500/40 text-rose-500" : ""}`}><Clock className="h-3 w-3" /> {g.expired ? "Expired" : g.expires_at}</Badge>}
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setManageId(g.id)} data-testid={`manage-gallery-${g.id}`}><Upload className="mr-1.5 h-3.5 w-3.5" /> Manage</Button>
                <Button size="sm" variant="ghost" onClick={() => copyLink(g.token)} data-testid={`copy-link-${g.id}`}><Link2 className="mr-1.5 h-3.5 w-3.5" /> Link</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateGalleryDialog open={createOpen} onOpenChange={setCreateOpen} />
      {manageId && <ManageGalleryDialog gid={manageId} onClose={() => setManageId(null)} onCopyLink={copyLink} />}
    </div>
  );
}

function CreateGalleryDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const EMPTY = { title: "", client_id: "", client_name: "", description: "", access_code: "", expires_at: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data, enabled: open });

  const create = async () => {
    setSaving(true);
    try {
      const payload = { title: form.title, client_id: form.client_id || null, client_name: form.client_name, description: form.description, access_code: form.access_code || null, expires_at: form.expires_at || null };
      await api.post("/deliverables", payload);
      toast.success("Gallery created");
      setForm(EMPTY); onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["deliverables"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pickClient = (id) => {
    const c = contacts.find((x) => x.id === id);
    setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : f.client_name }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setForm(EMPTY); }}>
      <DialogContent data-testid="create-gallery-dialog">
        <DialogHeader>
          <DialogTitle>New deliverable gallery</DialogTitle>
          <DialogDescription>Give it a name, optionally protect it with a code, then upload files after creating.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Smith Wedding — Final Photos" data-testid="gallery-title-input" /></div>
          <div className="space-y-1.5"><Label>Client <span className="font-normal text-muted-foreground">(optional)</span></Label>
            {contacts.length > 0 ? (
              <Select value={form.client_id} onValueChange={pickClient}>
                <SelectTrigger data-testid="gallery-client-select"><SelectValue placeholder="Link to a client" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" data-testid="gallery-client-input" />
            )}
          </div>
          <div className="space-y-1.5"><Label>Description <span className="font-normal text-muted-foreground">(optional)</span></Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="A short note shown to the client" data-testid="gallery-desc-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Access code <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} placeholder="Leave blank for open link" data-testid="gallery-code-input" /></div>
            <div className="space-y-1.5"><Label>Expires <span className="font-normal text-muted-foreground">(optional)</span></Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="gallery-expiry-input" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={create} disabled={!form.title || saving} data-testid="save-gallery-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create gallery</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageGalleryDialog({ gid, onClose, onCopyLink }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploads, setUploads] = useState({}); // name -> progress
  const { data: gallery, isLoading } = useQuery({ queryKey: ["deliverables", gid], queryFn: async () => (await api.get(`/deliverables/${gid}`)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["deliverables", gid] }); qc.invalidateQueries({ queryKey: ["deliverables"] }); };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    for (const file of files) {
      if (file.size > 250 * 1024 * 1024) { toast.error(`${file.name} is over the 250 MB limit.`); continue; }
      setUploads((u) => ({ ...u, [file.name]: 0 }));
      try {
        await uploadFile(gid, file, (p) => setUploads((u) => ({ ...u, [file.name]: p })));
        refresh();
      } catch (err) {
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail)}`);
      } finally {
        setUploads((u) => { const n = { ...u }; delete n[file.name]; return n; });
      }
    }
    toast.success("Upload complete");
  };

  const removeFile = async (fid) => {
    await api.delete(`/deliverables/files/${fid}`);
    refresh();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="manage-gallery-dialog">
        <DialogHeader>
          <DialogTitle>{gallery?.title || "Gallery"}</DialogTitle>
          <DialogDescription>Upload files and share the portal link with your client.</DialogDescription>
        </DialogHeader>
        {isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-border bg-secondary/40 p-3">
              <Link2 className="h-4 w-4 shrink-0 text-accent" />
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" data-testid="portal-link-text">{portalUrl(gallery.token)}</span>
              <Button size="sm" variant="outline" onClick={() => onCopyLink(gallery.token)} data-testid="manage-copy-link"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
            </div>

            <div>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="gallery-file-input" />
              <Button variant="outline" className="w-full" onClick={() => fileRef.current?.click()} data-testid="upload-files-button"><Upload className="mr-2 h-4 w-4" /> Upload files (up to 250 MB each)</Button>
            </div>

            {Object.entries(uploads).map(([name, prog]) => (
              <div key={name} className="space-y-1" data-testid={`upload-progress-${name}`}>
                <div className="flex justify-between text-xs text-muted-foreground"><span className="truncate">{name}</span><span>{prog}%</span></div>
                <Progress value={prog} className="h-1.5" />
              </div>
            ))}

            <div className="max-h-[40vh] space-y-1.5 overflow-y-auto">
              {(gallery.files || []).length === 0 && Object.keys(uploads).length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">No files yet — upload some above.</p>
              )}
              {(gallery.files || []).map((f) => {
                const Icon = fileIcon(f.content_type);
                return (
                  <div key={f.id} className="flex items-center gap-3 rounded-xl border border-border bg-background/50 p-2.5" data-testid={`gallery-file-${f.id}`}>
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{f.name}</p><p className="text-xs text-muted-foreground">{fmtSize(f.size)}</p></div>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeFile(f.id)} data-testid={`delete-file-${f.id}`}><X className="h-4 w-4" /></Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
